/**
 * Tabs that are links, because each one is a route (§4.2) — which also gives middle-click and
 * open-in-new-tab. Roving tabindex and arrow keys, and `aria-controls` on both tab levels: the
 * prototype's sub-tabs have no panel association at all.
 */

import { useRef, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { cx } from '~/lib/cx';
import styles from './Tabs.module.css';

export interface TabDefinition {
  key: string;
  label: string;
  href: string;
  count?: number | string;
}

export interface TabsProps {
  tabs: readonly TabDefinition[];
  /** The key of the selected tab, decided by the route rather than by this component. */
  value: string;
  size?: 'primary' | 'sub';
  'aria-label': string;
  /** The id of the panel these tabs control. */
  panelId: string;
  idPrefix: string;
}

export function Tabs({ tabs, value, size = 'primary', panelId, idPrefix, ...rest }: TabsProps) {
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);

  const move = (delta: number) => {
    const at = tabs.findIndex((tab) => tab.key === value);
    const next = tabs[(at + delta + tabs.length) % tabs.length];
    if (!next) return;
    void navigate(next.href);
    requestAnimationFrame(() => {
      listRef.current?.querySelector<HTMLElement>(`#${idPrefix}-${CSS.escape(next.key)}`)?.focus();
    });
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={rest['aria-label']}
      className={cx(styles.tabs, size === 'sub' && styles.sub)}
    >
      {tabs.map((tab) => {
        const selected = tab.key === value;
        return (
          <NavLink
            key={tab.key}
            id={`${idPrefix}-${tab.key}`}
            to={tab.href}
            role="tab"
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={selected ? 0 : -1}
            className={cx(styles.tab)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                move(1);
              }
              if (event.key === 'ArrowLeft') {
                event.preventDefault();
                move(-1);
              }
              if (event.key === 'Home') {
                event.preventDefault();
                move(-tabs.findIndex((t) => t.key === value));
              }
            }}
          >
            {tab.label}
            {tab.count === undefined ? null : <span className={styles.count}>{tab.count}</span>}
          </NavLink>
        );
      })}
    </div>
  );
}

export interface TabPanelProps {
  id: string;
  labelledBy: string;
  children: ReactNode;
}

export function TabPanel({ id, labelledBy, children }: TabPanelProps) {
  return (
    <div id={id} role="tabpanel" aria-labelledby={labelledBy} tabIndex={-1} className={cx(styles.panel)}>
      {children}
    </div>
  );
}
